// BIZH-1191 — SSO auth service
//
// Provides the exchangeSsoHandoff() function that POSTs the single-use
// handoff token to the backend and returns a LoginResponse.
//
// The backend endpoint /api/auth/sso/exchange returns a shape that is
// byte-identical to AuthController::login2fa()'s respondWithToken() output:
//   { status, access_token, user, token_type, expires_in }
//
// This type is intentionally shared with the legacy 2FA login path so
// JWTContext treats both responses identically.
//
// BIZH-1195 — adds logoutSso() which calls POST /api/auth/sso/logout and
// returns the Keycloak end-session URL so the caller can navigate there.

import axiosInstance from '../utils/axios';
import { AuthUser } from '../@types/auth';

// ----------------------------------------------------------------------

export type LoginResponse = {
  status: string;
  access_token: string;
  user: AuthUser;
  token_type: string;
  expires_in: number;
};

// ----------------------------------------------------------------------

/**
 * Exchange a single-use SSO handoff token for a Bizhaven JWT.
 *
 * Calls POST /api/auth/sso/exchange with { handoff }.
 * Returns the full LoginResponse on success.
 * Throws on any non-2xx HTTP status (axios interceptor normalises errors).
 *
 * The optional `signal` parameter is forwarded to axios so callers can
 * cancel the request via an AbortController (BIZH-1192 — the 30s recovery
 * "Start over" CTA aborts the in-flight exchange before navigating away).
 *
 * SECURITY: this function never logs the handoff value or the returned
 * access_token — all logging is the caller's responsibility, and the
 * caller (SsoCallback) must not log them either.
 */
export async function exchangeSsoHandoff(
  handoff: string,
  signal?: AbortSignal
): Promise<LoginResponse> {
  const response = await axiosInstance.post<LoginResponse>(
    '/auth/sso/exchange',
    { handoff },
    { signal }
  );
  return response.data;
}

/**
 * Invalidate the current SSO session on the backend and retrieve the
 * Keycloak end-session URL.
 *
 * Calls POST /api/auth/sso/logout with the JWT already attached via
 * axios.defaults.headers.common.Authorization (set by setSession()).
 * Returns { logoutUrl } — the caller must navigate to this URL so that
 * Keycloak clears its SSO cookie.
 *
 * Throws on any non-2xx HTTP status; the caller (JWTContext.logout) is
 * expected to catch and fall through to a local-only logout in that case.
 */
export async function logoutSso(): Promise<{ logoutUrl: string }> {
  const response = await axiosInstance.post<{ logoutUrl: string }>('/auth/sso/logout', {});
  return response.data;
}
